import { Item, Price } from 'shared-types';
import { AnalyticsEvent } from './analytics.types';
import { formatBasketItems, formatPrice, formatPurchaseParams, getBasketItemName } from './format';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
    gtag?: (...args: any[]) => void;
  }
}

export interface PropertyAnalyticsConfig {
  googleAnalyticsId?: string | null;
  gaid?: string | null; // Legacy GA property
  googleTagManager?: string | null;
}

type QueuedEvent = {
  event: AnalyticsEvent;
  propertyConfig?: PropertyAnalyticsConfig;
};

const GA4_ECOMMERCE_EVENTS = new Set<string>([
  'add_payment_info',
  'add_to_cart',
  'begin_checkout',
  'purchase',
  'remove_from_cart',
  'view_cart',
  'view_item',
]);

const FLAT_ITEM_KEYS = ['item_category', 'item_id', 'item_name'] as const;

const READY_FALLBACK_MS = 5000;

export function createAnalytics() {
  let enabled = true;
  let ready = false;
  let queue: QueuedEvent[] = [];
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function gtag(...args: any[]) {
    if (typeof window !== 'undefined') {
      window.dataLayer ??= [];

      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
  }

  function getEffectiveGoogleAnalyticsId(propertyConfig: PropertyAnalyticsConfig): string | null {
    // Priority order: googleAnalyticsId (GA4) > gaid (legacy) > null
    return propertyConfig.googleAnalyticsId || propertyConfig.gaid || null;
  }

  function withFlatItems(event: AnalyticsEvent): Record<string, any> {
    const params = event.params as Record<string, any>;

    if (GA4_ECOMMERCE_EVENTS.has(event.event)) {
      return params;
    }

    const items = Array.isArray(params.items) ? params.items : [];
    const [firstItem] = items;

    if (typeof firstItem !== 'object' || firstItem === null) {
      return params;
    }

    const flatItems: Record<string, any> = {};

    FLAT_ITEM_KEYS.forEach((key) => {
      if (firstItem[key] !== undefined) {
        flatItems[key] = firstItem[key];
      }
    });

    return { ...flatItems, ...params };
  }

  function send({ event, propertyConfig }: QueuedEvent) {
    const effectiveGA4Id = propertyConfig ? getEffectiveGoogleAnalyticsId(propertyConfig) : null;

    if (effectiveGA4Id) {
      gtag('config', effectiveGA4Id);
    }

    gtag('event', event.event, withFlatItems(event));
  }

  function track(event: AnalyticsEvent, propertyConfig?: PropertyAnalyticsConfig) {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    if (!ready) {
      queue.push({ event, propertyConfig });
      startFallback();
      return;
    }

    send({ event, propertyConfig });
  }

  function startFallback() {
    if (fallbackTimer !== null) {
      return;
    }

    fallbackTimer = setTimeout(markReady, READY_FALLBACK_MS);
  }

  function markReady() {
    if (ready) {
      return;
    }

    ready = true;

    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }

    const queued = queue;
    queue = [];

    queued.forEach((queuedEvent) => send(queuedEvent));
  }

  function trackToProperty(event: AnalyticsEvent, propertyConfig: PropertyAnalyticsConfig) {
    const effectiveGA4Id = getEffectiveGoogleAnalyticsId(propertyConfig);

    if (!enabled || !effectiveGA4Id) {
      return;
    }

    gtag('event', event.event, {
      ...event.params,
      send_to: effectiveGA4Id,
    });
  }

  function trackPurchase(event: AnalyticsEvent, propertyConfig: PropertyAnalyticsConfig) {
    if (!enabled) {
      return;
    }

    const effectiveGA4Id = getEffectiveGoogleAnalyticsId(propertyConfig);

    if (effectiveGA4Id) {
      trackToProperty(event, propertyConfig);
    }

    if (typeof window !== 'undefined' && window.dataLayer && propertyConfig.googleTagManager) {
      window.dataLayer.push({
        event: 'ga4_purchase',
        ecommerce: {
          ...event.params,
        },
        gtm_container_id: propertyConfig.googleTagManager,
        ga4_measurement_id: effectiveGA4Id,
      });
    }
  }

  function disableTracking() {
    enabled = false;
    queue = [];
  }

  function enableTracking() {
    enabled = true;
  }

  return {
    track,
    trackToProperty,
    trackPurchase,
    enableTracking,
    disableTracking,
    markReady,
    gtag,
  };
}

export const analytics = createAnalytics();

export function getPropertyAnalyticsConfig(property: any): PropertyAnalyticsConfig {
  if (!property) {
    return {
      googleAnalyticsId: null,
      gaid: null,
      googleTagManager: null,
    };
  }

  return {
    googleAnalyticsId: property.googleAnalyticsId || null,
    gaid: property.gaid || null,
    googleTagManager: property.googleTagManager || null,
  };
}

export function trackPurchaseEvent(
  order: any,
  property: any,
  source: 'payment_success' | 'confirmation_page'
): void {
  const items = Array.isArray(order?.items) ? order.items : [];

  if (!order?.total || !items.length || !property) {
    return;
  }

  const propertyConfig = getPropertyAnalyticsConfig(property);

  const purchaseEvent = {
    event: 'purchase' as const,
    source,
    params: formatPurchaseParams(order, items),
  };

  analytics.trackPurchase(purchaseEvent, propertyConfig);
}

export function trackRemoveFromCart(item: Item): void {
  const price = item.discount?.standardPrice || item.price;

  if (!price) {
    return;
  }

  analytics.track({
    event: 'remove_from_cart',
    params: {
      currency: price.currencyCode,
      value: formatPrice(price.amount),
      items: [
        {
          item_name: getBasketItemName(item),
          currency: price.currencyCode,
          price: formatPrice(price.amount),
        },
      ],
    },
  });
}

export function trackAddPaymentInfo(
  basket: { total?: Price | null; items?: unknown } | null | undefined,
  paymentType?: string
): void {
  const total = basket?.total;

  if (!total) {
    return;
  }

  analytics.track({
    event: 'add_payment_info',
    params: {
      currency: total.currencyCode,
      value: formatPrice(total.amount),
      items: formatBasketItems(Array.isArray(basket.items) ? basket.items : []),
      ...(paymentType ? { payment_type: paymentType } : {}),
    },
  });
}
